import React, { useState, useEffect } from 'react';
import { Modal, Form, Table, Button, Space, Input, Select, Toast, Popconfirm, JsonViewer } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';

const SpecialPages = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [formApi, setFormApi] = useState(null);
    const [typList, setTypList] = useState([]);

    const statusMap = {
        0: { text: '显示', type: 'success' },
        1: { text: '隐藏', type: 'danger' },
    };

    useEffect(() => {
        const fetchTypList = async () => {
            try {
                const response = await request.get('/go/sp/all/list_all_page_special_typ/');
                if (response.errno === 0) {
                    setTypList(response.list || []);
                } else {
                    Toast.error(response.errstr || '获取分类失败');
                }
            } catch (error) {
                console.error('获取分类失败:', error);
                Toast.error('获取分类失败');
            }
        };
        fetchTypList();
    }, []);

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/all/list_all_page_special/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (typList.length > 0) {
            fetchList();
        }
    }, [typList]);

    const handleSubmit = async (values) => {
        try {
            // 验证content是否为合法的JSON对象
            let content = values.content;
            if (typeof content === 'string') {
                try {
                    content = JSON.parse(content);
                } catch (error) {
                    Toast.error('内容格式不正确，请输入合法的JSON格式');
                    return;
                }
            } else if (typeof content !== 'object' || content === null) {
                Toast.error('内容必须是JSON对象格式');
                return;
            }

            const processedValues = {
                ...values,
                content: JSON.stringify(content),
                uid: currentRecord?.uid
            };
            const response = await request.post('/go/sp/all/set_all_page_special/', processedValues);
            if (response.errno === 0) {
                Toast.success(currentRecord ? '编辑成功' : '添加成功');
                setShowAddModal(false);
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || (currentRecord ? '编辑失败' : '添加失败'));
            }
        } catch (error) {
            console.error(currentRecord ? '编辑失败:' : '添加失败:', error);
            Toast.error(currentRecord ? '编辑失败' : '添加失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/all/delete_all_page/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的页面');
            return;
        }
        try {
            const response = await request.post('/go/sp/all/batchedit_all_page/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const columns = [
        {
            title: '页面信息',
            dataIndex: 'title',
            width: 300,
            render: (text, record) => (
                <div style={{ fontSize: '12px' }}>
                    <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{text}</div>
                    <div style={{ color: 'var(--semi-color-text-2)' }}>
                        {record.public?.name ? `所属小程序：${record.public.name}` : ''}
                    </div>
                    <div style={{ color: 'var(--semi-color-text-2)' }}>{typList.find(item => item.uid === record.typ)?.name || '-'}</div>
                </div>
            )
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Button theme='light' type={status.type} size='small'>{status.text}</Button>;
            }
        },
        {
            title: '排序',
            dataIndex: 'sort',
            width: 100
        },
        {
            title: '创建时间/更新时间',
            dataIndex: 'update_time',
            width: 180,
            render: (text, record) => (
                <div style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                    <div>{formatTimestamp(record.create_time)}</div>
                    <div>{formatTimestamp(text)}</div>
                </div>
            )
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该页面吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = () => {
        const initialValues = currentRecord ? {
            ...currentRecord,
            content: typeof currentRecord.content === 'string' ? JSON.parse(currentRecord.content) : (currentRecord.content || {})
        } : {
            public_uid: '0',
            typ: '',
            title: '',
            content: {},
            status: 0,
            sort: 0
        };

        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onSubmit={handleSubmit}
                style={{ padding: '20px' }}
            >
                <Form.Input
                    field="title"
                    label="页面名称"
                    rules={[{ required: true, message: '请输入页面名称' }]}
                />
                <Form.Select
                    field="typ"
                    label="页面类型"
                    style={{ width: '100%' }}
                    rules={[{ required: true, message: '请选择页面类型' }]}
                >
                    {typList.map((item) => (
                        <Select.Option key={item.uid} value={item.uid}>{item.name}</Select.Option>
                    ))}
                </Form.Select>
                <Form.InputNumber
                    field="sort"
                    label="排序"
                    min={0}
                    style={{ width: '100%' }}
                />
                <Form.Select
                    field="status"
                    label="状态"
                    style={{ width: '100%' }}
                >
                    {Object.entries(statusMap).map(([value, { text }]) => (
                        <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                    ))}
                </Form.Select>
                <Form.Slot label="内容">
                    <JsonViewer
                        value={typeof formApi?.getValue('content') === 'string' ? formApi?.getValue('content') : JSON.stringify(formApi?.getValue('content') || {}, null, 2)}
                        onChange={value => formApi?.setValue('content', value)}
                        height={300}
                        width="100%"
                        options={{
                            readOnly: false,
                            autoWrap: true,
                            minimap: { enabled: false }
                        }}
                    />
                </Form.Slot>
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => {
                            setShowAddModal(false);
                            setShowEditModal(false);
                        }}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    return (
        <div className={styles.specialPagesContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索页面名称"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="页面状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增页面</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='warning' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量隐藏</Button>
                            <Button type='primary' onClick={() => handleBatchEdit(0, selectedRowKeys)}>批量显示</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个页面吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增页面"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={800}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
            >
                {renderForm()}
            </Modal>

            <Modal
                title="编辑页面"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={800}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
            >
                {renderForm()}
            </Modal>
        </div>
    );
};

export default SpecialPages;